import { CommonModule } from '@angular/common';
import { Component, ElementRef, computed, inject, input, OnInit, output, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { CmUiChipComponent, CmUiEmptyStateComponent, CmUiStandardButtonComponent, UiLoaderComponent } from '@cm/ui-modules';
import { take } from 'rxjs/operators';
import { OrgChart, OrgLevelNode, LEVEL_TYPES } from '@modules/org-structure/models/org-chart.model';
import { OrgChartService } from '@modules/org-structure/services/org-chart.service';

export interface OrgLevelSelection {
  levelUuid: string;
  chartUuid: string;
  levelName: string;
  chartName: string;
}

interface StorageData {
  selections: OrgLevelSelection[];
  expandedChartUuids: string[];
}

let nextPanelId = 0;

@Component({
  selector: 'app-org-structure-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatCheckboxModule,
    MatIconModule,
    CmUiChipComponent,
    CmUiEmptyStateComponent,
    CmUiStandardButtonComponent,
    UiLoaderComponent,
  ],
  templateUrl: './org-structure-dropdown.component.html',
  styleUrl: './org-structure-dropdown.component.scss',
  host: { '(document:click)': 'onDocumentClick($event)', '(document:keydown.escape)': 'closePanel()' },
})
export class OrgStructureDropdownComponent implements OnInit {
  mode = input<'single' | 'multi' | 'one-per-chart'>('multi');
  placeholder = input<string>('ADMIN.orgStructureDropdown.placeholder');
  storageKey = input<string>('org-structure-dropdown');
  openAbove = input<boolean>(false);

  selectionChange = output<string[]>();
  selectionDataChange = output<OrgLevelSelection[]>();

  /** Id of the panel the field controls (aria-controls) — unique per instance. */
  readonly panelId = `org-dropdown-panel-${nextPanelId++}`;

  @ViewChild('trigger') triggerEl!: ElementRef<HTMLElement>;

  isOpen = signal(false);
  panelStyle = signal<Record<string, string>>({});
  charts = signal<OrgChart[]>([]);
  treesByChart = signal<Map<string, OrgLevelNode>>(new Map());
  expandedChartUuids = signal<Set<string>>(new Set());
  selections = signal<OrgLevelSelection[]>([]);
  loading = signal(false);
  loadingTrees = signal<Set<string>>(new Set());

  selectedLevelUuids = computed(() => new Set(this.selections().map(s => s.levelUuid)));

  private readonly levelTypeMap = new Map(LEVEL_TYPES.map(lt => [lt.value, lt.label]));
  private readonly orgChartService = inject(OrgChartService);
  private readonly elementRef = inject(ElementRef);

  ngOnInit(): void {
    this.restoreFromStorage();
    this.loadCharts();
    if (this.selections().length) {
      this.selectionChange.emit([...this.selectedLevelUuids()]);
      this.selectionDataChange.emit(this.selections());
    }
  }

  private restoreFromStorage(): void {
    try {
      const raw = localStorage.getItem(this.storageKey());
      if (!raw) return;
      const data: StorageData = JSON.parse(raw);
      this.selections.set(data.selections ?? []);
      this.expandedChartUuids.set(new Set(data.expandedChartUuids ?? []));
    } catch {
      // ignore malformed storage
    }
  }

  private saveToStorage(): void {
    const data: StorageData = {
      selections: this.selections(),
      expandedChartUuids: [...this.expandedChartUuids()],
    };
    localStorage.setItem(this.storageKey(), JSON.stringify(data));
  }

  private loadCharts(): void {
    this.loading.set(true);
    this.orgChartService.getAll().pipe(take(1)).subscribe({
      next: (charts) => {
        this.charts.set(charts);
        this.loading.set(false);
        const toLoad = new Set([
          ...this.selections().map(s => s.chartUuid),
          ...this.expandedChartUuids(),
        ]);
        toLoad.forEach(uuid => this.loadTreeForChart(uuid));
      },
    });
  }

  private loadTreeForChart(chartUuid: string): void {
    if (this.treesByChart().has(chartUuid)) return;
    this.loadingTrees.update(s => new Set([...s, chartUuid]));
    this.orgChartService.getTree(chartUuid).pipe(take(1)).subscribe({
      next: (root) => {
        this.treesByChart.update(m => new Map([...m, [chartUuid, root]]));
        this.loadingTrees.update(s => { const n = new Set(s); n.delete(chartUuid); return n; });
      },
    });
  }

  togglePanel(event: Event): void {
    event.stopPropagation();
    const willOpen = !this.isOpen();
    this.isOpen.set(willOpen);
    if (willOpen) {
      const chartUuidsWithSelections = new Set(this.selections().map(s => s.chartUuid));
      if (chartUuidsWithSelections.size > 0) {
        this.expandedChartUuids.update(s => new Set([...s, ...chartUuidsWithSelections]));
        chartUuidsWithSelections.forEach(uuid => this.loadTreeForChart(uuid));
      }
    }
  }

  /** Enter / Space on the field itself (not on a chip's × inside it) opens or closes the panel. */
  onTriggerKey(event: Event): void {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.togglePanel(event);
  }

  closePanel(): void {
    this.isOpen.set(false);
  }

  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }

  toggleChartExpand(chartUuid: string): void {
    const expanded = new Set(this.expandedChartUuids());
    if (expanded.has(chartUuid)) {
      expanded.delete(chartUuid);
    } else {
      expanded.add(chartUuid);
      this.loadTreeForChart(chartUuid);
    }
    this.expandedChartUuids.set(expanded);
    this.saveToStorage();
  }

  isChartExpanded(chartUuid: string): boolean {
    return this.expandedChartUuids().has(chartUuid);
  }

  isLevelSelected(levelUuid: string): boolean {
    return this.selectedLevelUuids().has(levelUuid);
  }

  isTreeLoading(chartUuid: string): boolean {
    return this.loadingTrees().has(chartUuid);
  }

  getChartTree(chartUuid: string): OrgLevelNode | undefined {
    return this.treesByChart().get(chartUuid);
  }

  getLevelTypeLabel(levelType: string | undefined): string {
    if (!levelType) return '';
    return this.levelTypeMap.get(levelType) ?? levelType;
  }

  toggleLevel(level: OrgLevelNode, chartUuid: string, chartName: string): void {
    const alreadySelected = this.isLevelSelected(level.orgLevelUuid);
    const entry: OrgLevelSelection = { levelUuid: level.orgLevelUuid, chartUuid, levelName: level.name, chartName };

    if (this.mode() === 'single') {
      this.selections.set(alreadySelected ? [] : [entry]);
    } else if (this.mode() === 'one-per-chart') {
      if (alreadySelected) {
        this.selections.update(s => s.filter(x => x.levelUuid !== level.orgLevelUuid));
      } else {
        this.selections.update(s => [...s.filter(x => x.chartUuid !== chartUuid), entry]);
      }
    } else {
      if (alreadySelected) {
        this.selections.update(s => s.filter(x => x.levelUuid !== level.orgLevelUuid));
      } else {
        this.selections.update(s => [...s, entry]);
      }
    }
    this.saveToStorage();
    this.selectionChange.emit([...this.selectedLevelUuids()]);
    this.selectionDataChange.emit(this.selections());
  }

  removeSelection(levelUuid: string): void {
    this.selections.update(s => s.filter(x => x.levelUuid !== levelUuid));
    this.saveToStorage();
    this.selectionChange.emit([...this.selectedLevelUuids()]);
    this.selectionDataChange.emit(this.selections());
  }

  clearAll(): void {
    this.selections.set([]);
    this.saveToStorage();
    this.selectionChange.emit([]);
    this.selectionDataChange.emit([]);
  }
}
